import React, { useCallback, useEffect, useState } from 'react';

import { useUserContext } from '../../../common/utils/context';
import { ServiceRunListView } from '../servicerun';

import DriverMyDayView from './DriverMyDayView';
import { buildEmployeeTrainingContext, canShowPickingWorkForMode, getEmployeeDayAccess } from './employeeDayAccess';
import EmployeeMyDayPreviewView from './EmployeeMyDayPreviewView';
import EmployeeMyDaySelectorDialog from './EmployeeMyDaySelectorDialog';
import PickerMyDayView from './PickerMyDayView';
import MyDayHubView from './MyDayHubView';
import useMyDayMode, { MY_DAY_MODE } from './useMyDayMode';

const MyDayView = () => {
	const { canAccessMyDayFeature, mode, refetchModeData, ticketScope } = useMyDayMode();
	const { user, tenant, isAVC, hasPermission } = useUserContext();
	const [selectedEmployee, setSelectedEmployee] = useState(null);
	const [trainingContext, setTrainingContext] = useState(null);
	const [isEmployeeSelectorOpen, setIsEmployeeSelectorOpen] = useState(false);
	const [isPickingWorkShown, setIsPickingWorkShown] = useState(false);
	const canShowPickingWork = canShowPickingWorkForMode({ user, hasPermission, mode });
	useEffect(() => {
		if (!canShowPickingWork) {
			setIsPickingWorkShown(false);
		}
	}, [canShowPickingWork]);
	const showPickingWork = useCallback(() => setIsPickingWorkShown(true), []);
	const hidePickingWork = useCallback(() => setIsPickingWorkShown(false), []);
	const previewScopeKey = `${tenant?.id || ''}:${user?.id || ''}`;
	const { canViewEmployeeDays, canEngageEmployeeRuns } = getEmployeeDayAccess({
		canAccessMyDayFeature,
		isAVC,
		user,
		hasPermission,
	});
	useEffect(() => {
		setSelectedEmployee(null);
		setTrainingContext(null);
		setIsEmployeeSelectorOpen(false);
	}, [canEngageEmployeeRuns, canViewEmployeeDays, previewScopeKey]);
	const openEmployeeSelector = useCallback(() => setIsEmployeeSelectorOpen(true), []);
	const closeEmployeeSelector = useCallback(() => setIsEmployeeSelectorOpen(false), []);
	const selectEmployee = useCallback((employee) => {
		setSelectedEmployee({
			...employee,
			previewScopeKey,
		});
		setIsEmployeeSelectorOpen(false);
	}, [previewScopeKey]);
	const closeEmployeePreview = useCallback(() => {
		setSelectedEmployee(null);
		setTrainingContext(null);
	}, []);
	const chooseEmployeeFromTraining = useCallback(() => {
		setTrainingContext(null);
		setIsEmployeeSelectorOpen(true);
	}, []);
	const exitTraining = useCallback(() => setTrainingContext(null), []);
	const engageEmployeeRun = useCallback((runId) => {
		const nextTrainingContext = canEngageEmployeeRuns && buildEmployeeTrainingContext({
			employee: selectedEmployee,
			runId,
			scopeKey: previewScopeKey,
		});
		if (nextTrainingContext) {
			setTrainingContext(nextTrainingContext);
		}
	}, [canEngageEmployeeRuns, previewScopeKey, selectedEmployee]);
	const viewEmployeeDay = canViewEmployeeDays ? openEmployeeSelector : undefined;
	const isEmployeePreviewOpen = Boolean(selectedEmployee &&
		selectedEmployee.previewScopeKey === previewScopeKey &&
		canViewEmployeeDays);
	const isTrainingOpen = Boolean(trainingContext &&
		trainingContext.scopeKey === previewScopeKey &&
		canEngageEmployeeRuns);

	let content;
	if (isTrainingOpen) {
		content = (
			<DriverMyDayView
				key={`${trainingContext.scopeKey}:${trainingContext.runId}`}
				trainingContext={trainingContext}
				onExitTraining={exitTraining}
				onChooseEmployee={chooseEmployeeFromTraining}
			/>
		);
	}
	else if (isEmployeePreviewOpen) {
		content = (
			<EmployeeMyDayPreviewView
				key={`${previewScopeKey}:${selectedEmployee.id}`}
				employee={selectedEmployee}
				onBack={closeEmployeePreview}
				onChooseEmployee={openEmployeeSelector}
				canEngage={canEngageEmployeeRuns}
				onEngage={engageEmployeeRun}
			/>
		);
	}
	else if (mode === MY_DAY_MODE.CLASSIC) {
		content = <ServiceRunListView refetchModeData={refetchModeData} />;
	}
	else if (canShowPickingWork && isPickingWorkShown) {
		content = <PickerMyDayView refetchModeData={refetchModeData} onBackToMyDay={hidePickingWork} />;
	}
	else if (mode === MY_DAY_MODE.PICKER) {
		content = <PickerMyDayView refetchModeData={refetchModeData} onViewEmployeeDay={viewEmployeeDay} />;
	}
	else if (mode === MY_DAY_MODE.DRIVER) {
		content = (
			<DriverMyDayView
				refetchModeData={refetchModeData}
				onViewEmployeeDay={viewEmployeeDay}
				onShowPickingWork={canShowPickingWork ? showPickingWork : undefined}
			/>
		);
	}
	else {
		content = (
			<MyDayHubView
				refetchModeData={refetchModeData}
				ticketScope={ticketScope}
				onViewEmployeeDay={viewEmployeeDay}
				onShowPickingWork={canShowPickingWork ? showPickingWork : undefined}
			/>
		);
	}

	return (
		<>
			{content}
			{
				canViewEmployeeDays && (
					<EmployeeMyDaySelectorDialog
						key={previewScopeKey}
						isOpen={isEmployeeSelectorOpen}
						canEngage={canEngageEmployeeRuns}
						onClose={closeEmployeeSelector}
						onSelect={selectEmployee}
					/>
				)
			}
		</>
	);
};

export default MyDayView;
